declare const BACKEND_URL: string;

import type { ChatMessage, AvailableToolsListType, ChatCompletionResponse, SessionsListResponse, CreateSessionResponse, GetSessionResponse, PageNameOption, SessionSummary, SessionRenameOptions } from './api.types';

const normalizeNullableString = (value?: string | PageNameOption | '' | null): string | null => {
  if (typeof value !== 'string') {
    return value ?? null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

export const login = async (username: string, password: string): Promise<string> => {
  const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
    method: 'POST',
    headers: {
      'X-Internal-Authorization': `Bearer ${btoa(`${username}:${password}`)}`,
      'Content-Type': 'application/json',
    },
  });
  if (!response.ok) {
    throw new Error('Login failed');
  }

  const { authToken } = await response.json();
  return authToken;
};

export const verifyToken = async (token: string): Promise<boolean> => {
  const response = await fetch(`${BACKEND_URL}/api/auth/verify-token`, {
    method: 'POST',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    return false;
  }

  const { valid, username, message } = await response.json();
  return valid;
};

export const sendChatRequest = async (
  messages: ChatMessage[],
  token: string,
  allowedTools: string[] = [],
  sessionId: string
): Promise<ChatCompletionResponse> => {
  try {
    const response = await fetch(`${BACKEND_URL}/api/builder/completion`, {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages,
        allowedTools,
        sessionId
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error("Server return error:", errorData);

      // Handle limit exceeded errors with special formatting
      if (errorData.error === 'LIMIT_EXCEEDED') {
        const limitInfo = errorData.limitType ? ` (${errorData.limitType})` : '';
        const usageInfo = errorData.currentValue !== undefined && errorData.maxValue !== undefined
          ? `\n\n📊 Current usage: ${errorData.currentValue.toLocaleString()} / ${errorData.maxValue.toLocaleString()}`
          : '';
        const limitClassInfo = errorData.limitClass && errorData.limitClass !== 'private'
          ? `\n🏷️ Your limit class: ${errorData.limitClass}`
          : '';
        throw new Error(`⚠️ Limit Exceeded${limitInfo}\n\n${errorData.message}${usageInfo}${limitClassInfo}`);
      }

      if (errorData.error) {
        throw new Error(errorData.error + ": " + errorData.message || `API request failed with status ${response.status}`);
      } else {
        throw new Error(errorData.message || `API request failed with status ${response.status}`);
      }
    }

    const parsedResponse = await response.json();

    parsedResponse.lastReply.suggestions = { type: '', options: [] };

    return parsedResponse as ChatCompletionResponse;
  } catch (error) {
    const err = error as Error;
    console.error('API request failed:', error);
    return {
      status: 'Error',
      //messages: [],
      lastToolsCalls: [],
      lastReply: {
        id: '',
        role: 'assistant',
        content: err.message || 'An error occurred while processing your request.',
        timestamp: new Date().toISOString(),
        duration: 0,
        is_liked: false,
        is_disliked: false
      },
      isConversationEnded: false,
      result: {},
      totalTokens: 0
    };
  }
};

export const fetchAvailableTools = async (token: string): Promise<AvailableToolsListType[]> => {
  const response = await fetch(`${BACKEND_URL}/api/builder/tools/available`, {
    method: 'GET',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API request failed with status ${response.status}`);
  }

  const parsedResponse = await response.json();
  if (parsedResponse.status !== 'OK') {
    throw new Error(parsedResponse.message || 'Failed to parse response from /api/builder/tools/available');
  }

  return parsedResponse.tools as AvailableToolsListType[];
}

// Sessions API
export const fetchSessions = async (token: string): Promise<SessionsListResponse['sessions']> => {
  const response = await fetch(`${BACKEND_URL}/api/builder/sessions`, {
    method: 'GET',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API request failed with status ${response.status}`);
  }

  const parsedResponse: SessionsListResponse = await response.json();
  if (parsedResponse.status !== 'OK') {
    throw new Error('Failed to fetch sessions list');
  }
  return parsedResponse.sessions;
};

export const createSession = async (
  token: string,
  name: string,
  allowedTools: string[] = [],
  language: string,
  websiteId?: string | null,
  pageName?: PageNameOption | '' | null
): Promise<CreateSessionResponse['session']> => {
  const normalizedWebsiteId = normalizeNullableString(websiteId);
  const normalizedPageName = normalizeNullableString(pageName);

  const response = await fetch(`${BACKEND_URL}/api/builder/sessions`, {
    method: 'POST',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      name,
      allowedTools,
      language,
      websiteId: normalizedWebsiteId,
      pageName: normalizedPageName
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API request failed with status ${response.status}`);
  }

  const parsedResponse: CreateSessionResponse = await response.json();
  if (parsedResponse.status !== 'OK') {
    throw new Error('Failed to create session');
  }
  return parsedResponse.session;
};

export const deleteSessionById = async (token: string, sessionId: string): Promise<void> => {
  const response = await fetch(`${BACKEND_URL}/api/builder/sessions/${encodeURIComponent(sessionId)}`, {
    method: 'DELETE',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API request failed with status ${response.status}`);
  }
};

export const fetchSessionById = async (token: string, sessionId: string, language?: string): Promise<GetSessionResponse> => {
  const url = new URL(`${BACKEND_URL}/api/builder/sessions/${encodeURIComponent(sessionId)}`);
  if (language) {
    url.searchParams.set('language', language);
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      'X-Internal-Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API request failed with status ${response.status}`);
  }

  const parsed: GetSessionResponse = await response.json();
  if (parsed.status !== 'OK') {
    throw new Error('Failed to fetch session');
  }
  return parsed;
};

export const setSessionContext = async (
  token: string,
  sessionId: string,
  { websiteId, pageName }: { websiteId?: string | null; pageName?: PageNameOption | '' | null }
): Promise<void> => {
  const normalizedWebsiteId = normalizeNullableString(websiteId);
  const normalizedPageName = normalizeNullableString(pageName);
  const response = await fetch(
    `${BACKEND_URL}/api/builder/sessions/${encodeURIComponent(sessionId)}/context`,
    {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        websiteId: normalizedWebsiteId,
        pageName: normalizedPageName,
      }),
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to update session context');
  }
};

const handleMessageActionResponse = async (response: Response, defaultError: string) => {
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || defaultError);
  }

  if (response.status === 204 || response.headers.get('Content-Length') === '0') {
    return undefined;
  }

  return response.json().catch(() => undefined);
};

export const updateMessageLikeStatus = async (
  token: string,
  sessionId: string,
  messageId: string,
  shouldLike: boolean
) => {
  const method = shouldLike ? 'POST' : 'DELETE';
  const response = await fetch(
    `${BACKEND_URL}/api/builder/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/like`,
    {
      method,
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  await handleMessageActionResponse(response, 'Failed to update like status');
};

export const updateMessageDislikeStatus = async (
  token: string,
  sessionId: string,
  messageId: string,
  shouldDislike: boolean
) => {
  const method = shouldDislike ? 'POST' : 'DELETE';
  const response = await fetch(
    `${BACKEND_URL}/api/builder/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}/dislike`,
    {
      method,
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  await handleMessageActionResponse(response, 'Failed to update dislike status');
};

export const deleteMessageById = async (
  token: string,
  sessionId: string,
  messageId: string
) => {
  const response = await fetch(
    `${BACKEND_URL}/api/builder/sessions/${encodeURIComponent(sessionId)}/messages/${encodeURIComponent(messageId)}`,
    {
      method: 'DELETE',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  await handleMessageActionResponse(response, 'Failed to delete message');
};

export const summarizeSession = async (
  token: string,
  sessionId: string
): Promise<SessionSummary> => {
  const response = await fetch(
    `${BACKEND_URL}/api/builder/sessions/${encodeURIComponent(sessionId)}/summarize`,
    {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to summarize session');
  }

  const parsed = await response.json();
  if (parsed.status !== 'OK' || !parsed.summary) {
    throw new Error(parsed.message || 'Unexpected summarize response');
  }

  return parsed.summary as SessionSummary;
};

export const renameSessionByAI = async (
  token: string,
  sessionId: string,
  force: boolean = false
): Promise<{ renameOptions: SessionRenameOptions | null; sessionName: string }> => {
  const response = await fetch(
    `${BACKEND_URL}/api/builder/sessions/${encodeURIComponent(sessionId)}/rename`,
    {
      method: 'POST',
      headers: {
        'X-Internal-Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ force })
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || 'Failed to rename session');
  }

  const parsed = await response.json();
  if (parsed.status !== 'OK' || !parsed.sessionName) {
    throw new Error(parsed.message || 'Unexpected rename response');
  }

  return {
    renameOptions: parsed.renameOptions ? parsed.renameOptions as SessionRenameOptions : null,
    sessionName: parsed.sessionName
  };
};
